import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  useTheme,
  IconButton,
  Tooltip,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { 
  Mail, 
  FileText, 
  Zap, 
  Globe, 
  Layers, 
  Link, 
  Settings, 
  MessageCircle, 
  HelpCircle, 
  BarChart3,
  Sliders,
  ArrowLeft,
  Save,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { LayoutGrid } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectApplicationScopes, 
  selectApplicationScopeLoading, 
  selectApplicationScopeError,
  setLoading,
  setApplicationScopes,
  setError,
  clearError
} from 'store/dataReducers/applicationScopeSlice';
import { getApplicationScope, saveApplicationScope } from 'apis/applicationScopeAPI';
import { ApplicationScopeSaveRequest, FuelTypeAllowance } from 'types/apis/applicationScopeInterfaces';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';

// Define fuel types
const FUEL_TYPES = ['Electric', 'Gas', 'Water', 'Dual Fuel'];

export const ApplicationScopePage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const toast = useToast();
  
  // Redux state
  const applicationScopes = useSelector(selectApplicationScopes);
  const loading = useSelector(selectApplicationScopeLoading);
  const error = useSelector(selectApplicationScopeError);
  const projectId = useSelector(selectSelectedPilotId);
  const [expandedApps, setExpandedApps] = useState<string[]>(['email-smart-alerts']);
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [expandedWidgets, setExpandedWidgets] = useState<Set<string>>(new Set());
  const [featureToggles, setFeatureToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [widgetToggles, setWidgetToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [viewToggles, setViewToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [allowedMeasurementTypes, setAllowedMeasurementTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch application scope data based on user attributes (from project/session settings)
  useEffect(() => {
    const fetchApplicationScope = async () => {
      dispatch(setLoading(true));
      try {
        const queryParams = projectId ? { project_id: projectId } : {} as any;
        
        const response = await getApplicationScope(rc, queryParams);
        console.log('response', response?.data);
        if (response.success) {
          dispatch(setApplicationScopes(response?.data?.applications));
          setAllowedMeasurementTypes(response?.data?.allowed_measurement_type);
          initializeFeatureTogglesFromAPI(response?.data?.applications);
        } else {
          dispatch(setError(response.error || 'Failed to fetch application scope'));
        }
      } catch (err: any) {
        dispatch(setError(err.message || 'An unexpected error occurred'));
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchApplicationScope();
  }, [projectId, dispatch, rc]);


  // Initialize feature toggles with prefilled data from API
  const initializeFeatureTogglesFromAPI = (apiData: any[]) => {
    const initialFeatureToggles: Record<string, Record<string, boolean>> = {};
    const initialWidgetToggles: Record<string, Record<string, boolean>> = {};
    const initialViewToggles: Record<string, Record<string, boolean>> = {};
    
    apiData.forEach(app => {
      app.features.forEach((feature: any) => {
        if (feature.selected_for) {
          // Map API fuel types to UI fuel types
          initialFeatureToggles[feature.name] = {
            'Electric': feature.selected_for.electric || false,
            'Gas': feature.selected_for.gas || false,
            'Water': feature.selected_for.water || false,
            'Dual Fuel': feature.selected_for.dual_fuel || false
          };
        }
        
        // Initialize widget toggles if widgets exist
        if (feature.widgets && Array.isArray(feature.widgets)) {
          feature.widgets.forEach((widget: any) => {
            if (widget.selected_for) {
              initialWidgetToggles[widget.name] = {
                'Electric': widget.selected_for.electric || false,
                'Gas': widget.selected_for.gas || false,
                'Water': widget.selected_for.water || false,
                'Dual Fuel': widget.selected_for.dual_fuel || false
              };
            }
            
            // Initialize view toggles if views exist
            if (widget.views && Array.isArray(widget.views)) {
              widget.views.forEach((view: any) => {
                if (view.selected_for) {
                  initialViewToggles[view.name] = {
                    'Electric': view.selected_for.electric || false,
                    'Gas': view.selected_for.gas || false,
                    'Water': view.selected_for.water || false,
                    'Dual Fuel': view.selected_for.dual_fuel || false
                  };
                }
              });
            }
          });
        }
      });
    });
    
    setFeatureToggles(initialFeatureToggles);
    setWidgetToggles(initialWidgetToggles);
    setViewToggles(initialViewToggles);
  };

  const handleAppExpansion = (appId: string) => {
    setExpandedApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleFeatureExpansion = (featureId: string) => {
    setExpandedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(featureId)) {
        newSet.delete(featureId);
      } else {
        newSet.add(featureId);
      }
      return newSet;
    });
  };

  const handleWidgetExpansion = (widgetId: string) => {
    setExpandedWidgets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(widgetId)) {
        newSet.delete(widgetId);
      } else {
        newSet.add(widgetId);
      }
      return newSet;
    });
  };

  // Helper to get all widgets and views for a feature
  const getFeatureChildren = (featureId: string) => {
    const widgets: string[] = [];
    const views: string[] = [];
    
    applicationScopes.forEach(app => {
      app.features.forEach(feature => {
        if (feature.name === featureId && feature.widgets) {
          feature.widgets.forEach(widget => {
            widgets.push(widget.name);
            if (widget.views) {
              widget.views.forEach(view => {
                views.push(view.name);
              });
            }
          });
        }
      });
    });
    
    return { widgets, views };
  };

  // Helper to get all views for a widget
  const getWidgetChildren = (widgetId: string) => {
    const views: string[] = [];
    
    applicationScopes.forEach(app => {
      app.features.forEach(feature => {
        if (feature.widgets) {
          feature.widgets.forEach(widget => {
            if (widget.name === widgetId && widget.views) {
              widget.views.forEach(view => {
                views.push(view.name);
              });
            }
          });
        }
      });
    });
    
    return views;
  };

  // Helper to get parent feature for a widget
  const getParentFeature = (widgetId: string) => {
    for (const app of applicationScopes) {
      for (const feature of app.features) {
        if (feature.widgets) {
          for (const widget of feature.widgets) {
            if (widget.name === widgetId) {
              return { featureId: feature.name, widgets: feature.widgets };
            }
          }
        }
      }
    }
    return null;
  };

  // Helper to get parent widget for a view
  const getParentWidget = (viewId: string) => {
    for (const app of applicationScopes) {
      for (const feature of app.features) {
        if (feature.widgets) {
          for (const widget of feature.widgets) {
            if (widget.views) {
              for (const view of widget.views) {
                if (view.name === viewId) {
                  return { widgetId: widget.name, featureId: feature.name, views: widget.views, allWidgets: feature.widgets };
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const handleToggleChange = (featureId: string, fuelType: string) => {
    const newValue = !featureToggles[featureId]?.[fuelType];
    const { widgets, views } = getFeatureChildren(featureId);
    
    // Update feature toggle
    setFeatureToggles(prev => ({
      ...prev,
      [featureId]: {
        ...prev[featureId],
        [fuelType]: newValue
      }
    }));
    
    // Cascade to all child widgets
    if (widgets.length > 0) {
      setWidgetToggles(prev => {
        const updated = { ...prev };
        widgets.forEach(widgetId => {
          updated[widgetId] = {
            ...updated[widgetId],
            [fuelType]: newValue
          };
        });
        return updated;
      });
    }
    
    // Cascade to all child views
    if (views.length > 0) {
      setViewToggles(prev => {
        const updated = { ...prev };
        views.forEach(viewId => {
          updated[viewId] = {
            ...updated[viewId],
            [fuelType]: newValue
          };
        });
        return updated;
      });
    }
    
    // Mark that changes have been made
    setHasChanges(true);
  };

  const handleWidgetToggleChange = (widgetId: string, fuelType: string) => {
    const newValue = !widgetToggles[widgetId]?.[fuelType];
    const childViews = getWidgetChildren(widgetId);
    const parentInfo = getParentFeature(widgetId);
    
    // Update widget toggle
    setWidgetToggles(prev => {
      const updated = {
        ...prev,
        [widgetId]: {
          ...prev[widgetId],
          [fuelType]: newValue
        }
      };
      
      // Check if we need to update parent feature
      if (parentInfo) {
        const { featureId, widgets } = parentInfo;
        
        if (newValue) {
          // If turning ON a widget, ensure parent feature is also ON
          setFeatureToggles(prevFeatures => ({
            ...prevFeatures,
            [featureId]: {
              ...prevFeatures[featureId],
              [fuelType]: true
            }
          }));
        } else {
          // If turning OFF, check if all sibling widgets are also OFF
          const allWidgetsOff = widgets.every(w => {
            if (w.name === widgetId) return true; // This one is being turned off
            return !updated[w.name]?.[fuelType];
          });
          
          if (allWidgetsOff) {
            // Turn off parent feature
            setFeatureToggles(prevFeatures => ({
              ...prevFeatures,
              [featureId]: {
                ...prevFeatures[featureId],
                [fuelType]: false
              }
            }));
          }
        }
      }
      
      return updated;
    });
    
    // Cascade to all child views
    if (childViews.length > 0) {
      setViewToggles(prev => {
        const updated = { ...prev };
        childViews.forEach(viewId => {
          updated[viewId] = {
            ...updated[viewId],
            [fuelType]: newValue
          };
        });
        return updated;
      });
    }
    
    // Mark that changes have been made
    setHasChanges(true);
  };

  const handleViewToggleChange = (viewId: string, fuelType: string) => {
    const newValue = !viewToggles[viewId]?.[fuelType];
    const parentInfo = getParentWidget(viewId);
    
    // Update view toggle
    setViewToggles(prev => {
      const updated = {
        ...prev,
        [viewId]: {
          ...prev[viewId],
          [fuelType]: newValue
        }
      };
      
      // Check if we need to update parent widget and feature
      if (parentInfo) {
        const { widgetId, featureId, views, allWidgets } = parentInfo;
        
        if (newValue) {
          // If turning ON a view, ensure parent widget and feature are also ON
          setWidgetToggles(prevWidgets => ({
            ...prevWidgets,
            [widgetId]: {
              ...prevWidgets[widgetId],
              [fuelType]: true
            }
          }));
          setFeatureToggles(prevFeatures => ({
            ...prevFeatures,
            [featureId]: {
              ...prevFeatures[featureId],
              [fuelType]: true
            }
          }));
        } else {
          // If turning OFF, check if all sibling views are also OFF
          const allViewsOff = views.every(v => {
            if (v.name === viewId) return true; // This one is being turned off
            return !updated[v.name]?.[fuelType];
          });
          
          if (allViewsOff) {
            // Turn off parent widget
            setWidgetToggles(prevWidgets => {
              const updatedWidgets = {
                ...prevWidgets,
                [widgetId]: {
                  ...prevWidgets[widgetId],
                  [fuelType]: false
                }
              };
              
              // Check if all widgets are now OFF to turn off parent feature
              const allWidgetsOff = allWidgets.every(w => {
                if (w.name === widgetId) return true; // This one is being turned off
                return !updatedWidgets[w.name]?.[fuelType];
              });
              
              if (allWidgetsOff) {
                setFeatureToggles(prevFeatures => ({
                  ...prevFeatures,
                  [featureId]: {
                    ...prevFeatures[featureId],
                    [fuelType]: false
                  }
                }));
              }
              
              return updatedWidgets;
            });
          }
        }
      }
      
      return updated;
    });
    
    // Mark that changes have been made
    setHasChanges(true);
  };

  const isFeatureEnabled = (featureId: string, fuelType: string) => {
    return featureToggles[featureId]?.[fuelType] || false;
  };

  const isWidgetEnabled = (widgetId: string, fuelType: string) => {
    return widgetToggles[widgetId]?.[fuelType] || false;
  };

  const isViewEnabled = (viewId: string, fuelType: string) => {
    return viewToggles[viewId]?.[fuelType] || false;
  };


  // Get icon mapping for applications (keep for display purposes until API provides icons)
  const getAppIcon = (appId: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'customer-web': <Globe size={20} />,
      'csr-console': <HelpCircle size={20} />,
      'smart-alerts': <Mail size={20} />,
      'smart-sms': <MessageCircle size={20} />,
      'smart-hers': <FileText size={20} />,
      'ev-managed-charging-app': <Zap size={20} />,
      'customer-web-widgets': <Layers size={20} />,
      'customer-web-api': <Link size={20} />,
      'bdr': <Settings size={20} />,
      'sms-alerts': <MessageCircle size={20} />,
      'customer-service-console': <HelpCircle size={20} />,
      'awb-analytics': <BarChart3 size={20} />,
      'system-api': <Sliders size={20} />
    };
    return iconMap[appId] || <Settings size={20} />;
  };
  // Prepare save data from current feature toggles
  const prepareSaveData = (): ApplicationScopeSaveRequest | null => {
    if (!projectId) {
      return null;
    }

    const applications: Array<{ master_data_id: number; allowed_for: FuelTypeAllowance }> = [];
    const visibleFuelTypes = getVisibleFuelTypes();
    
    // Iterate through all applications and features
    applicationScopes.forEach(app => {
      app.features.forEach(feature => {
        // Get saved selection with defaults
        const savedSelection = feature.selected_for || {
          electric: false, gas: false, water: false, dual_fuel: false
        };
        
        // Build current selection: use current toggle state for visible fuel types,
        // preserve saved values for non-visible fuel types
        const currentSelection: FuelTypeAllowance = {
          electric: isFeatureEnabled(feature.name, 'Electric'),
          gas: isFeatureEnabled(feature.name, 'Gas'),
          water: isFeatureEnabled(feature.name, 'Water'),
          dual_fuel: isFeatureEnabled(feature.name, 'Dual Fuel')
        };
        
        // Check if any visible fuel type toggles are enabled
        const hasEnabledToggles = visibleFuelTypes.some(fuelType => 
          isFeatureEnabled(feature.name, fuelType)
        );
        
        // Check if current selection differs from saved selection (only for visible fuel types)
        const hasChanges = visibleFuelTypes.some(fuelType => {
          const key = fuelType === 'Electric' ? 'electric' :
                     fuelType === 'Gas' ? 'gas' :
                     fuelType === 'Water' ? 'water' :
                     'dual_fuel';
          return currentSelection[key as keyof typeof currentSelection] !== 
                 savedSelection[key as keyof typeof savedSelection];
        });
        
        // Save if there are enabled toggles or if there are changes from saved state
        if (hasEnabledToggles || hasChanges) {
          applications.push({
            master_data_id: feature.master_data_id,
            allowed_for: currentSelection
          });
        }
        
        // Process widgets if they exist
        if (feature.widgets && Array.isArray(feature.widgets)) {
          feature.widgets.forEach(widget => {
            const widgetSavedSelection = widget.selected_for || {
              electric: false, gas: false, water: false, dual_fuel: false
            };
            
            const widgetCurrentSelection: FuelTypeAllowance = {
              electric: isWidgetEnabled(widget.name, 'Electric'),
              gas: isWidgetEnabled(widget.name, 'Gas'),
              water: isWidgetEnabled(widget.name, 'Water'),
              dual_fuel: isWidgetEnabled(widget.name, 'Dual Fuel')
            };
            
            const widgetHasEnabledToggles = visibleFuelTypes.some(fuelType => 
              isWidgetEnabled(widget.name, fuelType)
            );
            
            const widgetHasChanges = visibleFuelTypes.some(fuelType => {
              const key = fuelType === 'Electric' ? 'electric' :
                         fuelType === 'Gas' ? 'gas' :
                         fuelType === 'Water' ? 'water' :
                         'dual_fuel';
              return widgetCurrentSelection[key as keyof typeof widgetCurrentSelection] !== 
                     widgetSavedSelection[key as keyof typeof widgetSavedSelection];
            });
            
            if (widgetHasEnabledToggles || widgetHasChanges) {
              applications.push({
                master_data_id: widget.master_data_id,
                allowed_for: widgetCurrentSelection
              });
            }
            
            // Process views if they exist
            if (widget.views && Array.isArray(widget.views)) {
              widget.views.forEach(view => {
                const viewSavedSelection = view.selected_for || {
                  electric: false, gas: false, water: false, dual_fuel: false
                };
                
                const viewCurrentSelection: FuelTypeAllowance = {
                  electric: isViewEnabled(view.name, 'Electric'),
                  gas: isViewEnabled(view.name, 'Gas'),
                  water: isViewEnabled(view.name, 'Water'),
                  dual_fuel: isViewEnabled(view.name, 'Dual Fuel')
                };
                
                const viewHasEnabledToggles = visibleFuelTypes.some(fuelType => 
                  isViewEnabled(view.name, fuelType)
                );
                
                const viewHasChanges = visibleFuelTypes.some(fuelType => {
                  const key = fuelType === 'Electric' ? 'electric' :
                             fuelType === 'Gas' ? 'gas' :
                             fuelType === 'Water' ? 'water' :
                             'dual_fuel';
                  return viewCurrentSelection[key as keyof typeof viewCurrentSelection] !== 
                         viewSavedSelection[key as keyof typeof viewSavedSelection];
                });
                
                if (viewHasEnabledToggles || viewHasChanges) {
                  applications.push({
                    master_data_id: view.master_data_id,
                    allowed_for: viewCurrentSelection
                  });
                }
              });
            }
          });
        }
      });
    });
    
    if (applications.length === 0) {
      return null;
    }
    
    return {
      project_id: projectId,
      applications
    };
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!hasChanges) {
      return;
    }

    setSaving(true);

    try {
      const saveData = prepareSaveData();
      
      if (!saveData || saveData.applications.length === 0) {
        toast.warning('No changes to save');
        return;
      }

      const response = await saveApplicationScope(rc, saveData);
      
      toast.success(`Successfully saved ${response.total_processed} application scope settings`);
      setHasChanges(false);
      
    } catch (error: any) {
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Map API measurement types to UI fuel type names
  const mapMeasurementTypeToFuelType = (measurementType: string): string => {
    const mapping: Record<string, string> = {
      'ELECTRIC': 'Electric',
      'GAS': 'Gas',
      'WATER': 'Water',
      'DUAL_FUEL': 'Dual Fuel'
    };
    return mapping[measurementType] || measurementType;
  };

  // Get visible fuel types based on allowed_measurement_types from API response
  const getVisibleFuelTypes = () => {
    const mappedTypes = allowedMeasurementTypes.map(mapMeasurementTypeToFuelType);
    // If length is greater than or equal to 2, add Dual Fuel
    if (allowedMeasurementTypes.length >= 2 && !mappedTypes.includes('Dual Fuel')) {
      return [...mappedTypes, 'Dual Fuel'];
    }
    
    return mappedTypes;
  };

  // Helper function to sort items by sequence, using default index if sequence is null or missing
  const sortBySequence = <T extends { sequence?: number | null }>(
    items: T[]
  ): T[] => {
    // Separate items with sequence and without sequence
    const withSequence: Array<{ item: T; sequence: number; originalIndex: number }> = [];
    const withoutSequence: Array<{ item: T; originalIndex: number }> = [];
    
    items.forEach((item, index) => {
      if (item.sequence != null) {
        withSequence.push({ item, sequence: item.sequence, originalIndex: index });
      } else {
        withoutSequence.push({ item, originalIndex: index });
      }
    });
    
    // Sort items with sequence by their sequence value
    withSequence.sort((a, b) => a.sequence - b.sequence);
    
    // Sort items without sequence by their original index to maintain order
    withoutSequence.sort((a, b) => a.originalIndex - b.originalIndex);
    
    // Combine: items with sequence first, then items without sequence
    return [
      ...withSequence.map(w => w.item),
      ...withoutSequence.map(w => w.item)
    ];
  };

  // Get the display data - use API data directly
  const getDisplayData = () => {
    if (applicationScopes.length === 0) {
      // Return empty array when no API data - don't show static data
      return [];
    }

    // Filter applications that have features with product_key and enhance with display properties
    return applicationScopes
      .map(apiApp => {
        // Sort features by sequence
        const sortedFeatures = sortBySequence(
          apiApp.features.filter(apiFeature => apiFeature.product_key)
        );

        return {
          id: apiApp.id,
          name: apiApp.name,
          icon: getAppIcon(apiApp.id),
          features: sortedFeatures.map(apiFeature => {
            // Sort widgets by sequence
            const sortedWidgets = apiFeature.widgets
              ? sortBySequence(apiFeature.widgets)
              : undefined;

            return {
              id: apiFeature.name,
              name: apiFeature.display_name,
              // tooltip: getFeatureTooltip(apiFeature.name),
              is_allowed: apiFeature.is_allowed,
              allowed_for: apiFeature.allowed_for,
              requirements: apiFeature.requirements,
              widgets: sortedWidgets?.map(apiWidget => {
                // Sort views by sequence
                const sortedViews = apiWidget.views
                  ? sortBySequence(apiWidget.views)
                  : undefined;

                return {
                  name: apiWidget.name,
                  display_name: apiWidget.display_name,
                  is_allowed: apiWidget.is_allowed,
                  allowed_for: apiWidget.allowed_for || {
                    electric: false,
                    gas: false,
                    water: false,
                    dual_fuel: false
                  },
                  requirements: apiWidget.requirements,
                  master_data_id: apiWidget.master_data_id,
                  views: sortedViews?.map(apiView => ({
                    name: apiView.name,
                    display_name: apiView.display_name,
                    is_allowed: apiView.is_allowed,
                    allowed_for: apiView.allowed_for || {
                      electric: false,
                      gas: false,
                      water: false,
                      dual_fuel: false
                    },
                    requirements: apiView.requirements,
                    master_data_id: apiView.master_data_id
                  }))
                };
              })
            };
          })
        };
      })
      .filter(apiApp => apiApp.features.length > 0); // Only show applications that have features with product_key
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* edit project title */}
      <Typography variant="h4" component="h3" style={{ fontSize: '1.8rem', marginBottom: 2, marginLeft:32, textAlign: 'left'}}>
          Edit Project Setup
        </Typography>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}


        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Application Scope Section */}
        {!loading && (
          <Card sx={{ mt: 4,px:4, py:2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LayoutGrid size={30} style={{ stroke: theme.palette.primary.main }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Application Scope
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
              Enable and configure the applications and communications for this project across different fuel types.
            </Typography>

            {/* No Data State */}
            {getDisplayData().length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No application scope data available for the current project configuration.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Contact your administrator if you believe this is an error.
                </Typography>
              </Box>
            )}

            {/* Applications List */}
            <CardContent sx={{ display: 'flex',p:0, flexDirection: 'column' }}>
              {getDisplayData().map((app) => {
              const isExpanded = expandedApps.includes(app.id);
              const isEmailSmartAlerts = app.id === 'email-smart-alerts';
              
              return (
                <Box
                  key={app.id}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    mb: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Accordion 
                    expanded={isExpanded}
                    onChange={() => handleAppExpansion(app.id)}
                    sx={{
                      boxShadow: 'none',
                      bgcolor: theme.palette.secondary.main,
                      '&:before': { display: 'none' },
                      '& .MuiAccordionSummary-root': {
                        bgcolor: 'transparent',
                        borderRadius: 0,
                        minHeight: 'auto',
                        '&.Mui-expanded': {
                          minHeight: 'auto',
                        }
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        px: 3, 
                       
                        py: 2,
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          margin: 0,
                          '&.Mui-expanded': {
                            margin: 0,
                          }
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          color: theme.palette.text.secondary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,  }}>
                        <Box sx={{ color: theme.palette.primary.main }}>
                          {app.icon}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary'}}>
                          {app.name}
                        </Typography>
                        
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Table Structure */}
                      <Box sx={{ width: '100%' }}>
                        {/* Table Header */}
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: `360px 1fr repeat(${getVisibleFuelTypes().length}, 100px)`,
                          gap: 2,
                          px: 4,
                          py: 2,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          alignItems: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'left' }}>
                            Feature
                          </Typography>
                          <Box></Box>
                          {getVisibleFuelTypes().map(fuelType => (
                            <Typography key={fuelType} variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
                              {fuelType}
                            </Typography>
                          ))}
                        </Box>

                        {/* Table Rows */}
                        {app.features.map((feature, index) => {
                          const isFeatureExpanded = expandedFeatures.has(feature.id);
                          const hasWidgets = feature.widgets && feature.widgets.length > 0;
                          
                          return (
                            <Box key={feature.id}>
                              {/* Feature Row */}
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: `360px 1fr repeat(${getVisibleFuelTypes().length}, 100px)`,
                                gap: 2,
                                px: 4,
                                py: 2.5,
                                alignItems: 'center',
                                borderBottom: index < app.features.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                '&:hover': {
                                  bgcolor: theme.palette.secondary.light
                                }
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {hasWidgets && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleFeatureExpansion(feature.id)}
                                      sx={{ p: 0.5, color: 'text.secondary' }}
                                    >
                                      {isFeatureExpanded ? (
                                        <ChevronUp size={16} />
                                      ) : (
                                        <ChevronDown size={16} />
                                      )}
                                    </IconButton>
                                  )}
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary', textAlign: 'left' }}>
                                    {feature.name}
                                  </Typography>
                                  <Tooltip 
                                    title={
                                      <Box>
                                        {feature.requirements && (
                                          <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Requirements:</Typography>
                                            <Typography variant="caption" display="block">
                                              Customer: {feature.requirements.customer_type.join(', ')}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              Fuel: {feature.requirements.fuel_type.join(', ')}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              Meter: {feature.requirements.meter_type.join(', ')}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    } 
                                    arrow
                                  >
                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box></Box>
                                {getVisibleFuelTypes().map(fuelType => {
                                  // Map UI fuel types to API fuel types for permission checking
                                  const isEnabled = isFeatureEnabled(feature.id, fuelType);
                                  const isAllowed = feature.allowed_for[fuelType.toLowerCase() as keyof FuelTypeAllowance];
                                  
                                  return (
                                    isAllowed && (
                                    <Box key={fuelType} sx={{ display: 'flex', justifyContent: 'center' }}>
                                      <Switch
                                        checked={isEnabled}
                                        onChange={() => handleToggleChange(feature.id, fuelType)}
                                        disabled={!isAllowed}
                                        size="small"
                                        sx={{
                                          width: 38,
                                          height: 22,
                                          padding: 0,
                                          '& .MuiSwitch-switchBase': {
                                            padding: 0,
                                            margin: '2px',
                                            transitionDuration: '300ms',
                                            '&.Mui-checked': {
                                              transform: 'translateX(16px)',
                                              color: '#fff',
                                              '& + .MuiSwitch-track': {
                                                backgroundColor: isAllowed ? theme.palette.primary.main : theme.palette.action.disabled,
                                                opacity: 1,
                                                border: 0,
                                              },
                                              '& .MuiSwitch-thumb': {
                                                color: '#fff',
                                              },
                                            },
                                            '&.Mui-disabled': {
                                              '& .MuiSwitch-thumb': {
                                                color: theme.palette.action.disabled,
                                              },
                                              '& + .MuiSwitch-track': {
                                                backgroundColor: theme.palette.action.disabledBackground,
                                                opacity: 0.3,
                                              },
                                            },
                                            '&.Mui-focusVisible .MuiSwitch-thumb': {
                                              color: theme.palette.primary.main,
                                              border: '6px solid #fff',
                                            },
                                          },
                                          '& .MuiSwitch-thumb': {
                                            boxSizing: 'border-box',
                                            width: 18,
                                            height: 18,
                                            color: isAllowed ? '#fff' : theme.palette.action.disabled,
                                          },
                                          '& .MuiSwitch-track': {
                                            borderRadius: 22 / 2,
                                            backgroundColor: isAllowed ? '#E9E9EA' : theme.palette.action.disabledBackground,
                                            opacity: isAllowed ? 1 : 0.3,
                                            transition: theme.transitions.create(['background-color'], {
                                              duration: 500,
                                            }),
                                          },
                                        }}
                                      />
                                    </Box>)
                                  );
                                })}
                              </Box>
                              
                              {/* Widgets Section - Collapsible */}
                              {hasWidgets && (
                                <Collapse in={isFeatureExpanded}>
                                  <Box sx={{ bgcolor: theme.palette.secondary.dark }}>
                                    {feature.widgets?.map((widget, widgetIndex) => {
                                      const isWidgetExpanded = expandedWidgets.has(widget.name);
                                      const hasViews = widget.views && widget.views.length > 0;
                                      
                                      return (
                                        <Box key={widget.name}>
                                          {/* Widget Row */}
                                          <Box 
                                            sx={{ 
                                              display: 'grid',
                                              gridTemplateColumns: `360px 1fr repeat(${getVisibleFuelTypes().length}, 100px)`,
                                              gap: 2,
                                              px: 4,
                                              pl: 8, // Indent widgets
                                              py: 2,
                                              alignItems: 'center',
                                              borderBottom: widgetIndex < (feature.widgets?.length || 0) - 1 || hasViews ? `1px solid ${theme.palette.divider}` : 'none',
                                              '&:hover': {
                                                bgcolor: theme.palette.secondary.light
                                              }
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {hasViews && (
                                                <IconButton 
                                                  size="small" 
                                                  onClick={() => handleWidgetExpansion(widget.name)}
                                                  sx={{ p: 0.5, color: 'text.secondary' }}
                                                >
                                                  {isWidgetExpanded ? (
                                                    <ChevronUp size={16} />
                                                  ) : (
                                                    <ChevronDown size={16} />
                                                  )}
                                                </IconButton>
                                              )}
                                              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 13, color: 'text.secondary', textAlign: 'left' }}>
                                                {widget.display_name || widget.name}
                                              </Typography>
                                            </Box>
                                            <Box></Box>
                                            {getVisibleFuelTypes().map(fuelType => {
                                              const isEnabled = isWidgetEnabled(widget.name, fuelType);
                                              const isAllowed = widget.allowed_for?.[fuelType.toLowerCase() as keyof FuelTypeAllowance] || false;
                                              
                                              return (
                                                <Box key={fuelType} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                  <Switch
                                                    checked={isEnabled}
                                                    onChange={() => handleWidgetToggleChange(widget.name, fuelType)}
                                                    disabled={!isAllowed}
                                                    size="small"
                                                    sx={{
                                                      width: 38,
                                                      height: 22,
                                                      padding: 0,
                                                      '& .MuiSwitch-switchBase': {
                                                        padding: 0,
                                                        margin: '2px',
                                                        transitionDuration: '300ms',
                                                        '&.Mui-checked': {
                                                          transform: 'translateX(16px)',
                                                          color: '#fff',
                                                          '& + .MuiSwitch-track': {
                                                            backgroundColor: isAllowed ? theme.palette.primary.main : theme.palette.action.disabled,
                                                            opacity: 1,
                                                            border: 0,
                                                          },
                                                          '& .MuiSwitch-thumb': {
                                                            color: '#fff',
                                                          },
                                                        },
                                                        '&.Mui-disabled': {
                                                          '& .MuiSwitch-thumb': {
                                                            color: theme.palette.action.disabled,
                                                          },
                                                          '& + .MuiSwitch-track': {
                                                            backgroundColor: theme.palette.action.disabledBackground,
                                                            opacity: 0.3,
                                                          },
                                                        },
                                                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                                                          color: theme.palette.primary.main,
                                                          border: '6px solid #fff',
                                                        },
                                                      },
                                                      '& .MuiSwitch-thumb': {
                                                        boxSizing: 'border-box',
                                                        width: 18,
                                                        height: 18,
                                                        color: isAllowed ? '#fff' : theme.palette.action.disabled,
                                                      },
                                                      '& .MuiSwitch-track': {
                                                        borderRadius: 22 / 2,
                                                        backgroundColor: isAllowed ? '#E9E9EA' : theme.palette.action.disabledBackground,
                                                        opacity: isAllowed ? 1 : 0.3,
                                                        transition: theme.transitions.create(['background-color'], {
                                                          duration: 500,
                                                        }),
                                                      },
                                                    }}
                                                  />
                                                </Box>
                                              );
                                            })}
                                          </Box>
                                          
                                          {/* Views Section - Collapsible */}
                                          {hasViews && (
                                            <Collapse in={isWidgetExpanded}>
                                              <Box sx={{ bgcolor: theme.palette.secondary.dark }}>
                                                {widget.views?.map((view, viewIndex) => (
                                                  <Box 
                                                    key={view.name}
                                                    sx={{ 
                                                      display: 'grid',
                                                      gridTemplateColumns: `360px 1fr repeat(${getVisibleFuelTypes().length}, 100px)`,
                                                      gap: 2,
                                                      px: 4,
                                                      pl: 12, // Indent views more than widgets
                                                      py: 2,
                                                      alignItems: 'center',
                                                      borderBottom: viewIndex < (widget.views?.length || 0) - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                      '&:hover': {
                                                        bgcolor: theme.palette.secondary.light
                                                      }
                                                    }}
                                                  >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 12, color: 'text.secondary', textAlign: 'left' }}>
                                                        {view.display_name || view.name}
                                                      </Typography>
                                                    </Box>
                                                    <Box></Box>
                                                    {getVisibleFuelTypes().map(fuelType => {
                                                      const isEnabled = isViewEnabled(view.name, fuelType);
                                                      const isAllowed = view.allowed_for?.[fuelType.toLowerCase() as keyof FuelTypeAllowance] || false;
                                                      
                                                      return (
                                                        <Box key={fuelType} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                          <Switch
                                                            checked={isEnabled}
                                                            onChange={() => handleViewToggleChange(view.name, fuelType)}
                                                            disabled={!isAllowed}
                                                            size="small"
                                                            sx={{
                                                              width: 38,
                                                              height: 22,
                                                              padding: 0,
                                                              '& .MuiSwitch-switchBase': {
                                                                padding: 0,
                                                                margin: '2px',
                                                                transitionDuration: '300ms',
                                                                '&.Mui-checked': {
                                                                  transform: 'translateX(16px)',
                                                                  color: '#fff',
                                                                  '& + .MuiSwitch-track': {
                                                                    backgroundColor: isAllowed ? theme.palette.primary.main : theme.palette.action.disabled,
                                                                    opacity: 1,
                                                                    border: 0,
                                                                  },
                                                                  '& .MuiSwitch-thumb': {
                                                                    color: '#fff',
                                                                  },
                                                                },
                                                                '&.Mui-disabled': {
                                                                  '& .MuiSwitch-thumb': {
                                                                    color: theme.palette.action.disabled,
                                                                  },
                                                                  '& + .MuiSwitch-track': {
                                                                    backgroundColor: theme.palette.action.disabledBackground,
                                                                    opacity: 0.3,
                                                                  },
                                                                },
                                                                '&.Mui-focusVisible .MuiSwitch-thumb': {
                                                                  color: theme.palette.primary.main,
                                                                  border: '6px solid #fff',
                                                                },
                                                              },
                                                              '& .MuiSwitch-thumb': {
                                                                boxSizing: 'border-box',
                                                                width: 18,
                                                                height: 18,
                                                                color: isAllowed ? '#fff' : theme.palette.action.disabled,
                                                              },
                                                              '& .MuiSwitch-track': {
                                                                borderRadius: 22 / 2,
                                                                backgroundColor: isAllowed ? '#E9E9EA' : theme.palette.action.disabledBackground,
                                                                opacity: isAllowed ? 1 : 0.3,
                                                                transition: theme.transitions.create(['background-color'], {
                                                                  duration: 500,
                                                                }),
                                                              },
                                                            }}
                                                          />
                                                        </Box>
                                                      );
                                                    })}
                                                  </Box>
                                                ))}
                                              </Box>
                                            </Collapse>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Collapse>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </CardContent>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS)}
              sx={{ 
                color: 'text.secondary',
                borderColor: 'divider'
              }}
            >
              Back to Project Details
            </Button>
            
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
              onClick={handleSaveChanges}
              disabled={!hasChanges || saving}
              sx={{
                bgcolor: hasChanges ? 'primary.main' : 'grey.400',
                '&:hover': {
                  bgcolor: hasChanges ? 'primary.dark' : 'grey.500',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500'
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
          </Card>
        )}
      </Box>
    </Box>
  );
};